import { useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';

export interface TypeToConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmValue: string;
  confirmLabel: string;
  onConfirm: () => void;
  isPending?: boolean;
  confirmButtonLabel?: string;
  destructive?: boolean;
  children?: ReactNode;
  testId?: string;
}

export function TypeToConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmValue,
  confirmLabel,
  onConfirm,
  isPending = false,
  confirmButtonLabel,
  destructive = false,
  children,
  testId,
}: TypeToConfirmDialogProps) {
  const [inputValue, setInputValue] = useState('');

  const handleOpenChange = (v: boolean) => {
    if (isPending) return;
    if (!v) setInputValue('');
    onOpenChange(v);
  };

  const canConfirm = inputValue === confirmValue;

  const handleConfirm = () => {
    if (!canConfirm || isPending) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            {destructive && <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />}
            <DialogTitle className={destructive ? 'text-destructive' : undefined}>
              {title}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{description}</p>

          {children}

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{confirmLabel}</p>
            <p className="text-sm font-mono font-semibold text-foreground bg-muted rounded px-2 py-1 select-all">
              {confirmValue}
            </p>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isPending}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-testid={testId ? `${testId}-input` : undefined}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={!canConfirm || isPending}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {confirmButtonLabel ?? title}
              </>
            ) : (
              (confirmButtonLabel ?? title)
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
